import { FC, useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import Spacing from 'components/Spacing';
import { CheckoutForm } from './CheckoutForm';
import { MainContainer } from 'screens/Main/styled';
import { CheckoutContainer } from 'screens/Checkout/styled';
import { CandidateType } from 'components/Slate/Candidates/Candidates';
import { ContributionRules } from 'components/Slate/ContributionRules';
import { useAuth } from 'hooks/auth';
import {
  getUserProfile,
  postUserStripeCustomer,
  postUserStripeCustomerSession,
} from 'store/user/actions';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { getUserState } from 'selectors/user';
import { Elements } from '@stripe/react-stripe-js';
import { Toast } from 'components/Toast';
import { getIsTippingVariant2, getSelectedCandidatesWithValidAmount } from 'helpers/donate';
import { noop } from 'helpers/utils';
import { OathRecurringInterval } from 'store/pay/types';
import { useRedirectAfterLogin } from 'hooks/useRedirectAfterLogin';
import { useAuth0 } from '@auth0/auth0-react';
import { TicketWithQuantitySelected } from 'store/eventPage/types';
import { useDefaultTipAmount } from 'hooks/statsig';
interface Props {
  candidates: CandidateType[];
  clientId?: number;
  clientName?: string;
  donationAmount: number;
  isRecurring: boolean;
  pageId?: number;
  pageName?: string;
  pageOwner: string;
  pageQueryString?: string;
  recurringInterval: OathRecurringInterval;
  ticketSelections: TicketWithQuantitySelected[];
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_API_KEY as string);

export const StripeCheckout: FC<Props> = ({
  candidates,
  donationAmount,
  clientId,
  isRecurring,
  clientName,
  pageId,
  pageName,
  pageOwner,
  pageQueryString,
  recurringInterval = 'month',
  ticketSelections,
}) => {
  const dispatch = useAppDispatch();
  const userState = useAppSelector(getUserState);
  const { user } = useAuth0();
  const { handleUpdateCredentials } = useRedirectAfterLogin();
  const defaultTipAmount = useDefaultTipAmount(donationAmount, getIsTippingVariant2(clientId));
  const [donationAmountWithTip, setDonationAmountWithTip] = useState(
    donationAmount + defaultTipAmount
  );

  // Sync when defaultTipAmount resolves from Statsig (useState initializer only runs once)
  useEffect(() => {
    setDonationAmountWithTip(donationAmount + defaultTipAmount);
  }, [donationAmount, defaultTipAmount]);
  const [customerSessionClientSecret, setCustomerSessionClientSecret] = useState();

  useAuth(() => {
    dispatch(getUserProfile());
  });

  useEffect(() => {
    // create stripe customer if needed
    try {
      if (
        userState.data &&
        userState.data.first_name &&
        userState.data.last_name &&
        !userState.data.stripe_customer_id
      ) {
        dispatch(
          postUserStripeCustomer({
            name: `${userState.data.first_name} ${userState.data.last_name}`,
            email: userState.data.email,
          })
        );
      }
    } catch {
      noop(); // allow for checkout if this fails
    }
  }, [userState.data?.first_name, userState.data?.last_name, userState.data?.email]);

  useEffect(() => {
    if (userState.data?.stripe_customer_id) {
      postUserStripeCustomerSession().then(response => {
        setCustomerSessionClientSecret(response.data.customer_session_client_secret);
      });
    }
  }, [userState.data?.stripe_customer_id]);

  useEffect(() => {
    if (isRecurring && !user?.sub && !userState.isLoading && userState.data === null) {
      const checkoutLink = new URL(window.location.href);
      if (checkoutLink.pathname !== '/login') {
        handleUpdateCredentials(`${checkoutLink.pathname}${checkoutLink.search}`);
      }
      return;
    }
  }, [isRecurring, user?.sub, userState.isLoading, userState.data]);

  return (
    <MainContainer>
      <Elements
        options={{
          mode: isRecurring ? 'subscription' : 'payment',
          amount: Math.round(donationAmountWithTip * 100),
          currency: 'usd',
          customerSessionClientSecret,
        }}
        stripe={stripePromise}
      >
        <CheckoutContainer>
          <Toast /> {/* TODO: remove after real error and success handling complete */}
          <CheckoutForm
            candidates={getSelectedCandidatesWithValidAmount(candidates)}
            clientId={clientId}
            clientName={clientName}
            donationAmount={donationAmount}
            hasStripeSession={!!customerSessionClientSecret}
            isRecurring={isRecurring}
            pageId={pageId}
            pageName={pageName}
            pageOwner={pageOwner}
            pageQueryString={pageQueryString}
            recurringInterval={recurringInterval}
            setDonationAmountWithTip={setDonationAmountWithTip}
            ticketSelections={ticketSelections}
            user={userState.data}
          />
          <Spacing $size={70} />
          <ContributionRules />
          <Spacing $size={48.5} />
        </CheckoutContainer>
      </Elements>
    </MainContainer>
  );
};
